import { useState } from 'react'

import Head from 'next/head'

import Box from '@totallymoney/ui/components/Box'
import Flex from '@totallymoney/ui/components/Flex'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'
import Icon from '@totallymoney/ui/icons/Icon'
import theme from '@totallymoney/ui/theme'

import {
    StyledButton,
    StyledCard,
} from './UnscoredCategoryPageReviewsSection.styled'
import ReviewsPagination from './components/ReviewsPagination'
import StarRating from './components/StarRating'
import Trustpilot from './components/Trustpilot'
import { reviews } from './lib/reviews'

const UnscoredCategoryPageReviewsSection = () => {
    const [selected, setSelected] = useState(0)

    const isFirstItem = selected === 0
    const isLastItem = selected === reviews?.length - 1

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': reviews.map(({ name, rating, text }) => ({
                                '@type': 'Review',
                                author: { '@type': 'Person', name },
                                reviewRating: {
                                    '@type': 'Rating',
                                    ratingValue: rating,
                                    bestRating: 5,
                                },
                                itemReviewed: {
                                    '@type': 'Organization',
                                    name: 'TotallyMoney',
                                },
                                reviewBody: text,
                                publisher: {
                                    '@type': 'Organization',
                                    name: 'Trustpilot',
                                },
                            })),
                        }),
                    }}
                />
            </Head>

            <Box gridColumn={['1/13', '1/13', '2/12']}>
                <Box minHeight="118px">
                    <Trustpilot />
                </Box>

                <Box
                    display="grid"
                    gridTemplateColumns={[
                        'none',
                        `${theme.space40} 1fr ${theme.space40}`,
                    ]}
                    gridColumnGap="space50"
                    gridRowGap="space20"
                    gridAutoFlow="column"
                >
                    <StyledButton
                        aria-disabled={!!isFirstItem}
                        disabled={!!isFirstItem}
                        onClick={() =>
                            !isFirstItem && setSelected(selected - 1)
                        }
                        shouldShowMousePointer={!isFirstItem}
                    >
                        <Icon
                            icon="chevronLeft"
                            size={32}
                            color={isFirstItem ? theme.grey40 : theme.indigo60}
                        />
                    </StyledButton>

                    <Flex
                        overflow={['auto', 'unset']}
                        pr={['space10', 0]}
                        mb="space30"
                    >
                        {reviews.map(({ name, rating, text }, i) => (
                            <StyledCard
                                display={[
                                    'inline-block',
                                    selected === i ? 'block' : 'none',
                                ]}
                                p={['space30', 'space60']}
                                m={['space10', 0]}
                                mr="space20"
                                style={{ userSelect: 'none' }}
                                key={i}
                                minWidth="264px"
                                minHeight="208px"
                            >
                                <Stack space="space10">
                                    <Box>
                                        <StarRating rating={rating} size={24} />
                                    </Box>

                                    <Text variant="100">“{text}”</Text>

                                    <Text
                                        variant="100"
                                        weight="bold"
                                        textAlign="right"
                                    >
                                        {name}
                                    </Text>
                                </Stack>
                            </StyledCard>
                        ))}
                    </Flex>

                    <StyledButton
                        aria-disabled={!!isLastItem}
                        disabled={!!isLastItem}
                        onClick={() => !isLastItem && setSelected(selected + 1)}
                        shouldShowMousePointer={!isLastItem}
                    >
                        <Icon
                            icon="chevronRight"
                            size={32}
                            color={isLastItem ? theme.grey40 : theme.indigo60}
                        />
                    </StyledButton>
                </Box>

                <Box mb="space20">
                    <ReviewsPagination
                        numberOfReviews={reviews.length}
                        selected={selected}
                        setSelected={setSelected}
                    />
                </Box>

                <Text color="light" textAlign="center">
                    Showing our favourite reviews
                </Text>
            </Box>
        </>
    )
}

export default UnscoredCategoryPageReviewsSection
